import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const DISPLAY_DURATION = 5000;

export const NOTIFICATION_PRESETS = {
  success: {
    border: '5px solid #2ecc71',
    iconColor: '#2ecc71',
    progressBar: 'linear-gradient(to right, #2ecc71, #27ae60)',
    icon: '[check]',
    title: 'Téléchargement réussi',
    message: "Votre fichier a été traité avec succès.",
  },
  warning: {
    border: '5px solid #f39c12',
    iconColor: '#f39c12',
    progressBar: 'linear-gradient(to right, #f39c12, #e67e22)',
    icon: '[warning]',
    title: 'Avertissement',
    message: 'Veuillez vérifier les informations renseignées.',
  },
  error: {
    border: '5px solid #e74c3c',
    iconColor: '#e74c3c',
    progressBar: 'linear-gradient(to right, #e74c3c, #c0392b)',
    icon: '[x]',
    title: 'Erreur',
    message: 'Une erreur est survenue. Réessayez.',
  },
  info: {
    border: '5px solid #3498db',
    iconColor: '#3498db',
    progressBar: 'linear-gradient(to right, #3498db, #2980b9)',
    icon: '[info]',
    title: 'Information',
    message: "Voici les détails de l'opération en cours.",
  },
  premium: {
    border: '5px solid #9b59b6',
    iconColor: '#9b59b6',
    progressBar: 'linear-gradient(to right, #9b59b6, #8e44ad)',
    icon: '*',
    title: 'Premium',
    message: "Bénéficiez d'un traitement prioritaire.",
    background: 'linear-gradient(to bottom right, #f8f9fa, #e8eaf6)',
  },
};

const NotificationContext = createContext({
  notify: () => {},
  closeNotification: () => {},
});

export const useNotifications = () => useContext(NotificationContext);

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);

  const closeNotification = useCallback((id) => {
    setNotifications((prev) =>
      prev.map((notif) =>
        notif.id === id ? { ...notif, visible: false, closing: true, closedAt: Date.now() } : notif,
      ),
    );
  }, []);

  const notify = useCallback(({ type = 'info', title, message, duration = DISPLAY_DURATION }) => {
    const config = NOTIFICATION_PRESETS[type] || NOTIFICATION_PRESETS.info;
    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setNotifications((prev) => [
      ...prev,
      {
        id,
        type,
        title: title || config.title,
        message: message || config.message,
        duration,
        startedAt: Date.now(),
        progress: 0,
        remainingSeconds: Math.ceil(duration / 1000),
        visible: true,
        closing: false,
      },
    ]);
    return id;
  }, []);

  useEffect(() => {
    if (!notifications.length) return;
    const interval = setInterval(() => {
      setNotifications((prev) => {
        const now = Date.now();
        return prev
          .map((notif) => {
            if (notif.closing) {
              return now - notif.closedAt > 450 ? null : notif;
            }
            const elapsed = now - notif.startedAt;
            if (elapsed >= notif.duration) {
              return {
                ...notif,
                progress: 100,
                remainingSeconds: 0,
                visible: false,
                closing: true,
                closedAt: now,
              };
            }
            const progress = Math.min(100, Math.round((elapsed / notif.duration) * 100));
            const remainingSeconds = Math.max(0, Math.ceil((notif.duration - elapsed) / 1000));
            return { ...notif, progress, remainingSeconds };
          })
          .filter(Boolean);
      });
    }, 100);
    return () => clearInterval(interval);
  }, [notifications.length]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const originalAlert = window.alert;
    window.alert = (message = '') => {
      const text = typeof message === 'string' ? message : String(message ?? '');
      const normalized = text.toLowerCase();
      let type = 'info';
      if (normalized.includes('erreur') || normalized.includes('échec') || normalized.includes('[x]')) type = 'error';
      else if (normalized.includes('succès') || normalized.includes('[ok]')) type = 'success';
      else if (normalized.includes('avert') || normalized.includes('attention') || normalized.includes('[warning]')) type = 'warning';
      else if (normalized.includes('premium')) type = 'premium';
      notify({ type, title: 'Notification', message: text });
    };
    return () => {
      window.alert = originalAlert;
    };
  }, [notify]);

  const value = useMemo(() => ({ notify, closeNotification }), [notify, closeNotification]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="notification-viewport" aria-live="assertive">
        {notifications.map((notif) => {
          const config = NOTIFICATION_PRESETS[notif.type] || NOTIFICATION_PRESETS.info;
          return (
            <div
              key={notif.id}
              className={`notification-card ${notif.visible ? 'active' : ''}`}
              style={{
                border: config.border,
                background: config.background || '#fff',
              }}
            >
              <button
                className="notification-close-btn"
                onClick={() => closeNotification(notif.id)}
                aria-label="Fermer la notification"
              >
                ×
              </button>
              <div className="notification-modal-header">
                <div className="notification-modal-icon" style={{ color: config.iconColor }}>
                  {config.icon}
                </div>
                <div>
                  <h3>{notif.title}</h3>
                  <p>{notif.message}</p>
                </div>
              </div>
              <div className="notification-progress">
                <div
                  style={{
                    width: `${notif.progress}%`,
                    background: config.progressBar,
                  }}
                />
              </div>
              <p className="notification-countdown">Fermeture automatique dans {notif.remainingSeconds}s</p>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
}
